import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import type { GeoJSON, Feature, LineString, Polygon } from 'geojson';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useRef, useState } from 'react';
import { message, Modal, Radio } from 'antd';

import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_STYLE,
  DEFAULT_MAP_ZOOM,
  GL_COLOR_MB_ORANGE,
  GL_COLOR_SUCCESS,
  GL_COLOR_WHITE,
  PADDOCK_FILL_LAYER_ID,
  PADDOCK_LABELS_LAYER_ID,
  PADDOCK_LABELS_SOURCE_ID,
  PADDOCK_MAP_CONTAINER_ID,
  PADDOCK_OUTLINE_LAYER_ID,
  PADDOCK_SOURCE_ID,
} from 'constants/mapConstants';
import {
  MAP_BOUNDARY_MIN_POINTS_MESSAGE,
  MAP_BOUNDARY_SELF_INTERSECTS_MESSAGE,
  MAP_CUTOUT_MIN_POINTS_MESSAGE,
  MAP_CUTOUT_OUTSIDE_BOUNDARY_MESSAGE,
  MAP_CUTOUT_SELF_INTERSECTS_MESSAGE,
  MAP_HOLE_OUTSIDE_BOUNDARY_MESSAGE,
  MAP_HOLE_POINT_OUTSIDE_BOUNDARY_MESSAGE,
  MAP_HOLE_SEGMENT_CROSSES_HOLE_MESSAGE,
  MAP_HOLES_OVERLAP_MESSAGE,
  MAP_SPLIT_CUT_INVALID_MESSAGE,
  MAP_SPLIT_NO_VALID_CUTS_MESSAGE,
  MAP_VERTEX_DELETE_MIN_VERTICES_MESSAGE,
} from 'constants/messageConstants';
import { addPaddockLayersToMap } from '../mapLayers';
import {
  calculateAreaInHectares,
  deselectAllFeatures,
  featuresToFeatureCollection,
  featuresToLabelPointCollection,
  getPaddockSource,
  mergePaddockGeometriesMultiple,
  setupMapControls,
  splitPolygonByPolyline,
} from '../mapUtils';
import { makeLocalFeature } from '../types';
import type { MapFeature } from '../types';
import ShortcutHint, {
  CmdKey,
  ShortcutKey,
} from 'components/common/ShortcutHint/ShortcutHint';
import MapAttributionButton from './MapAttributionButton';
import MapNavToolbar from './MapNavToolbar';
import MapToolbar from './MapToolbar';
import PaddockContextMenu from './PaddockContextMenu';
import PaddockDisambiguateMenu from './PaddockDisambiguateMenu';
import PaddockInfoPanel from './PaddockInfoPanel';
import PaddockNewNameModal from './PaddockNewNameModal';
import PaddockRenameModal from './PaddockRenameModal';
import PaddockUpdateAreaModal from './PaddockUpdateAreaModal';

// Initialize Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN ?? '';
// Use the pre-built CSP worker to bypass webpack's worker chunk generation.
// webpack 5 splits mapbox-gl's dynamic import() into a shared worker chunk and
// then Terser minifies it, renaming the spread helper `r` out of scope in the
// worker thread (ReferenceError: r is not defined at loadTile). Pointing
// workerUrl at Mapbox's own pre-built worker avoids that code path entirely.
mapboxgl.workerUrl = `${process.env.PUBLIC_URL || ''}/mapbox-gl-csp-worker.js`;

// ─── Vertex snapping helpers ─────────────────────────────────
const SNAP_TOLERANCE_PX = 15;

// ─── Right-drag cursor ───────────────────────────────────────
function makeRightDragCursor(rightBtnFill: string, rightBtnStroke?: string): string {
  const btnPath = rightBtnStroke
    ? `<path d="M20,13 A6,6 0 0 1 26,19 L26,22 L20,22 Z" fill="${rightBtnFill}" stroke="${rightBtnStroke}" stroke-width="1"/>`
    : `<path d="M20,13 A6,6 0 0 1 26,19 L26,22 L20,22 Z" fill="${rightBtnFill}"/>`;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">` +
    `<polygon points="20,2 17,8 23,8" fill="#1f2937" stroke="white" stroke-width="2" stroke-linejoin="round" paint-order="stroke"/>` +
    `<polygon points="20,38 17,32 23,32" fill="#1f2937" stroke="white" stroke-width="2" stroke-linejoin="round" paint-order="stroke"/>` +
    `<polygon points="2,21 8,18 8,24" fill="#1f2937" stroke="white" stroke-width="2" stroke-linejoin="round" paint-order="stroke"/>` +
    `<polygon points="38,21 32,18 32,24" fill="#1f2937" stroke="white" stroke-width="2" stroke-linejoin="round" paint-order="stroke"/>` +
    `<rect x="14" y="13" width="12" height="16" rx="6" fill="white"/>` +
    btnPath +
    `<line x1="14.5" y1="22" x2="25.5" y2="22" stroke="#555" stroke-width="0.8"/>` +
    `<line x1="20" y1="13.5" x2="20" y2="22" stroke="#555" stroke-width="0.8"/>` +
    `<rect x="18.5" y="15" width="3" height="5" rx="1.5" fill="#888"/>` +
    `<rect x="14" y="13" width="12" height="16" rx="6" fill="none" stroke="#333" stroke-width="1.5"/>` +
    `</svg>`;
  return `url('data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}') 20 21, move`;
}
const CURSOR_RIGHT_DRAG_HOVER  = makeRightDragCursor('#555');
const CURSOR_RIGHT_DRAG_ACTIVE = makeRightDragCursor(GL_COLOR_SUCCESS);

/** Nearest saved-paddock vertex within SNAP_TOLERANCE_PX of cursor (screen px). */
function findSnapTarget(
  cursor: { x: number; y: number },
  map: mapboxgl.Map,
  candidates: [number, number][]
): [number, number] | null {
  let best: [number, number] | null = null;
  let bestDist = SNAP_TOLERANCE_PX;
  for (const coord of candidates) {
    const px = map.project(coord as mapboxgl.LngLatLike);
    const d = Math.hypot(px.x - cursor.x, px.y - cursor.y);
    if (d < bestDist) { bestDist = d; best = coord; }
  }
  return best;
}

function getDrawSnapCandidates(
  fc: GeoJSON.FeatureCollection<Polygon>,
  draw: MapboxDraw
): [number, number][] {
  const out: [number, number][] = [];
  for (const f of fc.features) {
    const ring = f.geometry.coordinates[0];
    for (let i = 0; i < ring.length - 1; i++) out.push([ring[i][0], ring[i][1]]);
  }
  for (const f of draw.getAll().features) {
    if (f.geometry.type !== 'Polygon') continue;
    const ring = (f.geometry as Polygon).coordinates[0];
    if (ring.length >= 5) out.push([ring[0][0], ring[0][1]]);
  }
  return out;
}

function getEditSnapCandidates(
  fc: GeoJSON.FeatureCollection<Polygon>,
  editingId: string
): [number, number][] {
  const out: [number, number][] = [];
  for (const f of fc.features) {
    if (f.properties?.id === editingId) continue;
    const ring = f.geometry.coordinates[0];
    for (let i = 0; i < ring.length - 1; i++) out.push([ring[i][0], ring[i][1]]);
  }
  return out;
}

/** Stable draw feature ID used for the in-progress split line preview. */
const SPLIT_PREVIEW_ID = 'split-preview';
/** Stable draw feature ID used for the in-progress cutout polygon preview. */
const CUTOUT_PREVIEW_ID = 'cutout-preview';
/** Stable draw feature ID used for the in-progress new-boundary polygon preview. */
const DRAW_PREVIEW_ID = 'draw-preview';

const SPLIT_PEN_CURSOR = (() => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">` +
    `<path fill="white" stroke="white" stroke-width="3" stroke-linejoin="round" stroke-linecap="round" d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>` +
    `<path fill="#1f2937" d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>` +
    `<path fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1" stroke-linecap="round" d="M15 5l4 4"/>` +
    `<line x1="2" y1="23" x2="22" y2="23" stroke="#1f2937" stroke-width="1.5" stroke-dasharray="3,2" stroke-linecap="round"/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 2 22, crosshair`;
})();

function polygonsEqual(a: Polygon | null | undefined, b: Polygon | null | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a.coordinates) === JSON.stringify(b.coordinates);
}

export type PaddockMapMode =
  | 'view'
  | 'draw'
  | 'edit'
  | 'merge'
  | 'split'
  | 'cutout';

interface Props {
  features: MapFeature[];
  highlightedZoneId: string | null;
  onPaddockSelect: (paddockId: string) => void;
}

const PaddockMapCanvas = ({ features, highlightedZoneId, onPaddockSelect }: Props) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const modeRef = useRef<PaddockMapMode>('view');
  const prevModeRef = useRef<PaddockMapMode>('view');
  const clearPaddockSelectionRef = useRef<(() => void) | null>(null);
  const paddocksToMergeRef = useRef<MapFeature[]>([]);
  const splitLinesRef = useRef<Feature<LineString>[]>([]);
  const splitFirstPointRef = useRef<[number, number] | null>(null);
  const handleSplitApplyRef = useRef<() => void>(() => {});
  const cutoutVerticesRef = useRef<[number, number][]>([]);
  const finalizeCutoutRef = useRef<() => void>(() => {});
  const drawVerticesRef = useRef<[number, number][]>([]);
  const finalizePaddockDrawRef = useRef<() => void>(() => {});
  const handleConfirmDrawPaddockRef = useRef<
    ((feature: Feature<Polygon>) => void) | null
  >(null);
  const selectedPaddockRef = useRef<MapFeature | null>(null);
  const drawPaddockNameRef = useRef<string | null>(null);
  const exitingEditRef = useRef(false);
  const snapEnabledRef = useRef(true);
  const snapTargetRef = useRef<[number, number] | null>(null);
  const altKeyRef = useRef(false);
  const undoStackRef = useRef<Polygon[]>([]);
  const redoStackRef = useRef<Polygon[]>([]);
  const draftGeometryRef = useRef<Feature<Polygon> | null>(null);
  const pushToUndoRef = useRef<() => void>(() => {});
  const handleUndoRef = useRef<() => void>(() => {});
  const handleRedoRef = useRef<() => void>(() => {});
  const rightButtonHeldRef = useRef(false);
  /** Kept current so the map-init closure always calls the latest prop. */
  const onPaddockSelectRef = useRef(onPaddockSelect);

  const paddockFeatureCollectionRef = useRef<GeoJSON.FeatureCollection<Polygon>>({
    type: 'FeatureCollection',
    features: [],
  });
  /** Local mutable copy of the features prop — all CRUD ops mutate this. */
  const localFeaturesRef = useRef<MapFeature[]>(features);

  const [mode, setMode] = useState<PaddockMapMode>('view');
  const [selectedPaddock, setSelectedPaddock] = useState<MapFeature | null>(null);
  const [draftGeometry, setDraftGeometry] = useState<Feature<Polygon> | null>(null);
  const [drawPaddockName, setDrawPaddockName] = useState<string | null>(null);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);
  const [paddocksToMerge, setPaddocksToMerge] = useState<MapFeature[]>([]);
  const [splitLineCount, setSplitLineCount] = useState(0);
  const [cutoutVertexCount, setCutoutVertexCount] = useState(0);
  const [drawVertexCount, setDrawVertexCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMergeConfirmModal, setShowMergeConfirmModal] = useState(false);
  const [mergeSelectedName, setMergeSelectedName] = useState('');
  const [mergeCustomName, setMergeCustomName] = useState('');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    paddock: MapFeature;
  } | null>(null);
  const [disambiguate, setDisambiguate] = useState<{
    x: number;
    y: number;
    paddocks: MapFeature[];
  } | null>(null);
  const disambiguateRef = useRef<{ x: number; y: number; paddocks: MapFeature[] } | null>(null);
  const [draftName, setDraftName] = useState('');
  const [renamePaddock, setRenamePaddock] = useState<MapFeature | null>(null);
  const [updateAreaPaddock, setUpdateAreaPaddock] = useState<MapFeature | null>(null);
  const [mapTipsVisible, setMapTipsVisible] = useState(false);
  const [mapTipsFading, setMapTipsFading] = useState(false);

  // Phase 1: all CRUD ops are synchronous local mutations — no loading state.
  const isLoading = false;

  /**
   * Synchronously updates both Mapbox GeoJSON sources and the local features ref.
   * Replaces the POC's async reloadPaddocks().
   */
  function updateMapSources(updatedFeatures: MapFeature[], selectedIds?: Set<string>) {
    localFeaturesRef.current = updatedFeatures;
    const fc = featuresToFeatureCollection(updatedFeatures);
    paddockFeatureCollectionRef.current = fc;
    const src = getPaddockSource(mapRef.current);
    if (src) src.setData(fc);
    const ids = selectedIds ?? (() => {
      const s = new Set<string>();
      if (selectedPaddockRef.current) s.add(selectedPaddockRef.current.id);
      paddocksToMergeRef.current.forEach(p => s.add(p.id));
      return s;
    })();
    const labelSrc = mapRef.current?.getSource(PADDOCK_LABELS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (labelSrc) labelSrc.setData(featuresToLabelPointCollection(updatedFeatures, ids));
  }

  /**
   * If all paddock names share a common prefix of the form "Foo - N", returns that prefix.
   */
  function computeSuggestedMergeName(paddocks: MapFeature[]): string | null {
    if (paddocks.length < 2) return null;
    const stripped = paddocks.map(p =>
      p.name.replace(/\s*[-–]\s*\d+\s*$/, '').trim()
    );
    const prefix = stripped[0];
    if (!prefix || !stripped.every(s => s === prefix)) return null;
    if (paddocks.some(p => p.name === prefix)) return null;
    return prefix;
  }

  // Show map tips once the Mapbox style has loaded, then fade after 5 s.
  useEffect(() => {
    let fadeStart: ReturnType<typeof setTimeout>;
    let remove: ReturnType<typeof setTimeout>;
    // Poll until mapRef is set and the style is loaded.
    const poll = setInterval(() => {
      if (!mapRef.current?.isStyleLoaded()) return;
      clearInterval(poll);
      setMapTipsVisible(true);
      fadeStart = setTimeout(() => setMapTipsFading(true), 5000);
      remove   = setTimeout(() => setMapTipsVisible(false), 5600);
    }, 200);
    return () => {
      clearInterval(poll);
      clearTimeout(fadeStart);
      clearTimeout(remove);
    };
  }, []);

  // Keep label source in sync when selection changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const labelSource = map.getSource(PADDOCK_LABELS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (!labelSource || !localFeaturesRef.current.length) return;

    const selectedIds = new Set<string>();
    if (selectedPaddock) selectedIds.add(selectedPaddock.id);
    paddocksToMerge.forEach(p => selectedIds.add(p.id));

    labelSource.setData(featuresToLabelPointCollection(localFeaturesRef.current, selectedIds));
  }, [selectedPaddock, paddocksToMerge]);

  // Sync mode ref with state
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  useEffect(() => {
    paddocksToMergeRef.current = paddocksToMerge;
  }, [paddocksToMerge]);
  useEffect(() => {
    selectedPaddockRef.current = selectedPaddock;
  }, [selectedPaddock]);
  useEffect(() => {
    drawPaddockNameRef.current = drawPaddockName;
  }, [drawPaddockName]);
  useEffect(() => {
    disambiguateRef.current = disambiguate;
  }, [disambiguate]);
  useEffect(() => {
    snapEnabledRef.current = snapEnabled;
    if (!snapEnabled) {
      snapTargetRef.current = null;
      const src = mapRef.current?.getSource('snap-indicator') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [snapEnabled]);
  useEffect(() => {
    draftGeometryRef.current = draftGeometry;
  }, [draftGeometry]);
  useEffect(() => {
    onPaddockSelectRef.current = onPaddockSelect;
  }, [onPaddockSelect]);

  // Sync prop features → localFeaturesRef in view mode (org switch / refetch).
  useEffect(() => {
    if (modeRef.current !== 'view') return;
    localFeaturesRef.current = features;
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    updateMapSources(features);
  }, [features]);

  // Tree → map sync: highlight the zone matching the tree-selected paddock.
  useEffect(() => {
    if (!highlightedZoneId) return;
    // Already showing this zone — zoneId unchanged (e.g. paddock → input → same paddock).
    if (selectedPaddockRef.current?.id === highlightedZoneId) return;
    const feature = localFeaturesRef.current.find((f) => f.id === highlightedZoneId);
    if (!feature) return;
    setSelectedPaddock(feature);
    const map = mapRef.current;
    if (map?.isStyleLoaded()) {
      deselectAllFeatures(map, paddockFeatureCollectionRef.current);
      map.setFeatureState(
        { source: PADDOCK_SOURCE_ID, id: feature.id },
        { selected: true }
      );
      updateMapSources(localFeaturesRef.current, new Set([feature.id]));
    }
  }, [highlightedZoneId]);

  // Initialise draftName when entering edit mode; clear it on exit.
  useEffect(() => {
    if (mode === 'edit' && selectedPaddock) {
      setDraftName(selectedPaddock.name);
    } else if (mode !== 'edit') {
      setDraftName('');
    }
  }, [mode]);

  // ─── Map initialization ───────────────────────────────────────────────────
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: PADDOCK_MAP_CONTAINER_ID,
      style: DEFAULT_MAP_STYLE,
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM,
      attributionControl: false,
    });

    // Custom direct_select mode — vertex/midpoint drag only.
    const mbdModes = MapboxDraw.modes;
    const VertexOnlyDirectSelect: MapboxDraw.DrawCustomMode = {
      ...mbdModes.direct_select,
      onMouseDown(state, e) {
        type ExtendedEvent = typeof e & { featureTarget?: { properties?: { meta?: string } } };
        const meta = (e as ExtendedEvent).featureTarget?.properties?.meta;
        if (meta === 'vertex' || meta === 'midpoint') {
          mbdModes.direct_select.onMouseDown?.call(this, state, e);
        }
      },
    };

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      modes: { ...mbdModes, direct_select: VertexOnlyDirectSelect },
    });

    setupMapControls(map, draw);

    function loadPaddocks() {
      updateMapSources(localFeaturesRef.current);
      const featureCollection = paddockFeatureCollectionRef.current;
      if (featureCollection.features.length > 0) {
        const bbox = turf.bbox(featureCollection);
        map.fitBounds(
          [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
          { padding: { top: 50, bottom: 50, left: 50, right: 50 }, maxZoom: 18, duration: 0 }
        );
      }
    }

    const handleMapLoad = () => {
      map.addSource(PADDOCK_SOURCE_ID, {
        type: 'geojson',
        data: paddockFeatureCollectionRef.current,
        promoteId: 'id',
        tolerance: 0,
      });

      map.addSource(PADDOCK_LABELS_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      addPaddockLayersToMap(map);

      // Snap indicator circle
      map.addSource('snap-indicator', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'snap-indicator-circle',
        type: 'circle',
        source: 'snap-indicator',
        paint: {
          'circle-radius': 7,
          'circle-color': GL_COLOR_SUCCESS,
          'circle-stroke-width': 2,
          'circle-stroke-color': GL_COLOR_WHITE,
          'circle-pitch-alignment': 'map',
        },
      });

      // Cutout vertex dots
      map.addSource('cutout-vertices', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'cutout-vertices-circle',
        type: 'circle',
        source: 'cutout-vertices',
        paint: {
          'circle-radius': 5,
          'circle-color': GL_COLOR_WHITE,
          'circle-stroke-width': 2,
          'circle-stroke-color': GL_COLOR_SUCCESS,
          'circle-pitch-alignment': 'map',
        },
      });

      // Override MapboxDraw vertex colours
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vertexInnerExpr: any = [
        'case', ['==', ['get', 'active'], 'true'], GL_COLOR_SUCCESS, GL_COLOR_MB_ORANGE,
      ];
      for (const suffix of ['.cold', '.hot']) {
        map.setPaintProperty(`gl-draw-vertex-inner${suffix}`, 'circle-color', vertexInnerExpr);
      }

      // New-boundary vertex dots
      map.addSource('draw-vertices', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'draw-vertices-circle',
        type: 'circle',
        source: 'draw-vertices',
        paint: {
          'circle-radius': 5,
          'circle-color': GL_COLOR_WHITE,
          'circle-stroke-width': 2,
          'circle-stroke-color': GL_COLOR_SUCCESS,
          'circle-pitch-alignment': 'map',
        },
      });

      loadPaddocks();
    };

    const handlePaddockClick = (e: mapboxgl.MapMouseEvent) => {
      if (!e.features?.length) return;
      const currentMode = modeRef.current;
      if (currentMode !== 'view' && currentMode !== 'merge') return;
      e.originalEvent?.stopPropagation();

      if (currentMode === 'view') {
        const allFeatures = map.queryRenderedFeatures(e.point, {
          layers: [PADDOCK_FILL_LAYER_ID],
        });
        const seen = new Set<string>();
        const overlapping: MapFeature[] = [];
        for (const f of allFeatures) {
          const pid = String(f.id);
          if (seen.has(pid)) continue;
          seen.add(pid);
          const mapFeat = localFeaturesRef.current.find(mf => mf.id === pid);
          if (!mapFeat) continue;
          overlapping.push(mapFeat);
        }
        if (overlapping.length > 1) {
          setDisambiguate({ x: e.point.x, y: e.point.y, paddocks: overlapping });
          return;
        }
      }

      const paddockId = e.features[0].id as string;
      const clickedPaddock = localFeaturesRef.current.find(f => f.id === paddockId);
      if (!clickedPaddock) return;

      if (currentMode === 'merge') {
        const current = paddocksToMergeRef.current;
        const isAlreadySelected = current.some(p => p.id === paddockId);
        const next = isAlreadySelected
          ? current.filter(p => p.id !== paddockId)
          : [...current, clickedPaddock];
        paddocksToMergeRef.current = next;
        setPaddocksToMerge(next);
        deselectAllFeatures(map, paddockFeatureCollectionRef.current);
        next.forEach(p => {
          map.setFeatureState(
            { source: PADDOCK_SOURCE_ID, id: p.id },
            { selected: true }
          );
        });
        setSelectedPaddock(next.length > 0 ? next[next.length - 1] : null);
        return;
      }

      deselectAllFeatures(map, paddockFeatureCollectionRef.current);
      map.setFeatureState(
        { source: PADDOCK_SOURCE_ID, id: paddockId },
        { selected: true }
      );
      setSelectedPaddock(clickedPaddock);
      if (clickedPaddock.paddockId) {
        onPaddockSelectRef.current(clickedPaddock.paddockId);
      }
    };

    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      const currentMode = modeRef.current;
      if (currentMode !== 'view' && currentMode !== 'merge') return;

      const feats = map.queryRenderedFeatures(e.point, {
        layers: [PADDOCK_FILL_LAYER_ID],
      });

      if (feats.length === 0) {
        if (currentMode === 'merge') return;
        deselectAllFeatures(map, paddockFeatureCollectionRef.current);
        setSelectedPaddock(null);
      }
    };

    const handleDrawCreate = (e: { features: Feature<Polygon | LineString>[] }) => {
      const feature = e.features[0];
      if (!feature) return;
      if (feature.geometry.type !== 'Polygon') return;
      if (modeRef.current !== 'draw') return;
      handleConfirmDrawPaddockRef.current?.(feature as Feature<Polygon>);
    };

    const handleDrawUpdate = (e: { features: Feature<Polygon>[] }) => {
      if (modeRef.current !== 'edit') return;
      if (drawUpdateActive) return;
      if (rightDragActive) return;
      if (leftDragActive) {
        pushToUndoRef.current();
        leftDragActive = false;
      }
      let feature = e.features[0] as Feature<Polygon>;
      const snapTarget = snapTargetRef.current;
      if (snapTarget && snapEnabledRef.current && !altKeyRef.current) {
        const srcRing = feature.geometry.coordinates[0];
        const targetPx = map.project(snapTarget as mapboxgl.LngLatLike);
        let nearestIdx = -1;
        let nearestDistPx = Infinity;
        for (let i = 0; i < srcRing.length - 1; i++) {
          const vPx = map.project(srcRing[i] as mapboxgl.LngLatLike);
          const d = Math.hypot(vPx.x - targetPx.x, vPx.y - targetPx.y);
          if (d < nearestDistPx) { nearestDistPx = d; nearestIdx = i; }
        }
        if (nearestIdx >= 0 && nearestDistPx < SNAP_TOLERANCE_PX) {
          const ring = srcRing.map(c => [...c] as number[]);
          ring[nearestIdx] = [...snapTarget];
          if (nearestIdx === 0) ring[ring.length - 1] = [...snapTarget];
          const correctedGeometry: Polygon = {
            type: 'Polygon',
            coordinates: [ring, ...feature.geometry.coordinates.slice(1)],
          };
          feature = { ...feature, geometry: correctedGeometry } as Feature<Polygon>;
          drawUpdateActive = true;
          draw.add({
            type: 'Feature',
            id: feature.id as string,
            geometry: correctedGeometry,
            properties: feature.properties ?? {},
          });
          drawUpdateActive = false;
        }
      }
      setDraftGeometry(feature);
    };

    const handlePreventContextMenu = (e: mapboxgl.MapMouseEvent) => {
      e.originalEvent.preventDefault();
    };

    const handlePaddockContextMenu = (e: mapboxgl.MapMouseEvent) => {
      if (modeRef.current !== 'view') return;
      e.originalEvent.preventDefault();
      if (!e.features?.length) return;

      const paddockId = e.features[0].id as string;
      const clickedPaddock = localFeaturesRef.current.find(f => f.id === paddockId);
      if (!clickedPaddock) return;

      deselectAllFeatures(map, paddockFeatureCollectionRef.current);
      map.setFeatureState(
        { source: PADDOCK_SOURCE_ID, id: paddockId },
        { selected: true }
      );
      setSelectedPaddock(clickedPaddock);
      setContextMenu({ x: e.point.x, y: e.point.y, paddock: clickedPaddock });
    };

    const handleDismissOverlays = () => {
      setContextMenu(null);
      if (disambiguateRef.current) {
        deselectAllFeatures(map, paddockFeatureCollectionRef.current);
        setDisambiguate(null);
      }
    };

    const handleDrawModeChange = () => {
      const currentMode = draw.getMode();
      if (currentMode === 'simple_select') {
        if (modeRef.current === 'draw') {
          setMode('view');
        } else if (modeRef.current === 'edit') {
          if (exitingEditRef.current) {
            // Intentional exit — clearPaddockSelection handles cleanup.
          } else {
            const paddock = selectedPaddockRef.current;
            if (paddock) {
              draw.changeMode('direct_select', { featureId: paddock.id });
            }
          }
        } else if (modeRef.current === 'merge') {
          setMode('view');
          setPaddocksToMerge([]);
          paddocksToMergeRef.current = [];
          if (clearPaddockSelectionRef.current) clearPaddockSelectionRef.current();
        }
      }
    };

    // ─── Canvas-level snap interceptors ──────────────────────
    const canvas = map.getCanvas();
    let snapRedirecting = false;
    let rightDragActive = false;
    let rightDragLastLngLat: mapboxgl.LngLat | null = null;
    let drawUpdateActive = false;
    let leftDragActive = false;
    let rightDragPreGeometry: Polygon | null = null;
    let rightDragMoved = false;
    let rightDragUndoCountAtStart = 0;

    const updateSnapUI = (target: [number, number] | null) => {
      snapTargetRef.current = target;
      const src = map.getSource('snap-indicator') as mapboxgl.GeoJSONSource | undefined;
      if (!src) return;
      if (target) {
        src.setData({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: target },
          properties: {},
        } as GeoJSON.GeoJSON);
      } else {
        src.setData({ type: 'FeatureCollection', features: [] });
      }
    };

    const updateDrawVertexDots = (vertices: [number, number][]) => {
      const src = map.getSource('draw-vertices') as mapboxgl.GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: vertices.map(coord => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: coord },
          properties: {},
        })),
      });
    };

    const updateCutoutVertexDots = (vertices: [number, number][]) => {
      const src = map.getSource('cutout-vertices') as mapboxgl.GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: vertices.map(coord => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: coord },
          properties: {},
        })),
      });
    };

    const handleSnapMousemove = (e: MouseEvent) => {
      if (modeRef.current === 'split') {
        canvas.style.cursor = SPLIT_PEN_CURSOR;
        e.stopImmediatePropagation();
        if (splitFirstPointRef.current) {
          const rect = canvas.getBoundingClientRect();
          const lngLat = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
          draw.add({
            id: SPLIT_PREVIEW_ID,
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [splitFirstPointRef.current, [lngLat.lng, lngLat.lat]],
            },
            properties: {},
          } as Feature<LineString>);
        }
        return;
      }
      if (modeRef.current === 'draw' && drawPaddockNameRef.current !== null) {
        canvas.style.cursor = SPLIT_PEN_CURSOR;
        e.stopImmediatePropagation();
        if (snapEnabledRef.current && !altKeyRef.current) {
          const rect = canvas.getBoundingClientRect();
          const cursor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          const candidates = getDrawSnapCandidates(paddockFeatureCollectionRef.current, draw);
          const target = findSnapTarget(cursor, map, candidates);
          updateSnapUI(target);
        } else {
          updateSnapUI(null);
        }
        return;
      }
      if (modeRef.current === 'cutout') {
        canvas.style.cursor = SPLIT_PEN_CURSOR;
        e.stopImmediatePropagation();
        return;
      }
      if (snapRedirecting) return;
      const currentMode = modeRef.current;
      if (currentMode !== 'draw' && currentMode !== 'edit') {
        updateSnapUI(null);
        return;
      }
      if (!snapEnabledRef.current || altKeyRef.current) {
        updateSnapUI(null);
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const cursor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const candidates =
        currentMode === 'draw'
          ? getDrawSnapCandidates(paddockFeatureCollectionRef.current, drawRef.current!)
          : getEditSnapCandidates(
              paddockFeatureCollectionRef.current,
              selectedPaddockRef.current?.id ?? ''
            );
      const target = findSnapTarget(cursor, map, candidates);
      updateSnapUI(currentMode === 'edit' ? target : null);

      if (target && (currentMode === 'draw' || (currentMode === 'edit' && (e.buttons & 1)))) {
        const px = map.project(target as mapboxgl.LngLatLike);
        e.stopImmediatePropagation();
        snapRedirecting = true;
        canvas.dispatchEvent(
          new MouseEvent('mousemove', {
            bubbles: true, cancelable: true, view: window,
            clientX: rect.left + px.x, clientY: rect.top + px.y,
            button: 0, buttons: e.buttons,
            ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, metaKey: e.metaKey,
          })
        );
        snapRedirecting = false;
      }
    };

    const handleSnapClick = (e: MouseEvent) => {
      if (modeRef.current === 'draw' && drawPaddockNameRef.current !== null) {
        e.stopImmediatePropagation();
        if (e.detail > 1) return;
        const snapTarget = snapTargetRef.current;
        let pt: [number, number];
        if (snapTarget && snapEnabledRef.current && !altKeyRef.current) {
          pt = snapTarget;
        } else {
          const rect = canvas.getBoundingClientRect();
          const lngLat = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
          pt = [lngLat.lng, lngLat.lat];
        }
        drawVerticesRef.current = [...drawVerticesRef.current, pt];
        const verts = drawVerticesRef.current;
        setDrawVertexCount(verts.length);
        updateDrawVertexDots(verts);
        draw.delete(DRAW_PREVIEW_ID);
        if (verts.length >= 2) {
          draw.add({
            id: DRAW_PREVIEW_ID,
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: verts },
            properties: {},
          } as Feature<LineString>);
        }
        return;
      }
      if (modeRef.current === 'cutout') {
        e.stopImmediatePropagation();
        if (e.detail > 1) return;
        const rect = canvas.getBoundingClientRect();
        const lngLat = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
        const pt: [number, number] = [lngLat.lng, lngLat.lat];

        const paddock = selectedPaddockRef.current;
        if (paddock) {
          const paddockFeature: Feature<Polygon> = {
            type: 'Feature',
            geometry: paddock.geometry,
            properties: {},
          };
          if (!turf.booleanPointInPolygon(turf.point(pt), paddockFeature)) {
            message.error(MAP_HOLE_POINT_OUTSIDE_BOUNDARY_MESSAGE, 3);
            return;
          }

          const prevVerts = cutoutVerticesRef.current;
          if (prevVerts.length >= 1 && paddock.geometry.coordinates.length > 1) {
            const prevPt = prevVerts[prevVerts.length - 1];
            const segment = turf.lineString([prevPt, pt]);
            let crossesHole = false;
            for (let i = 1; i < paddock.geometry.coordinates.length; i++) {
              const holeOutline = turf.lineString(
                paddock.geometry.coordinates[i] as [number, number][]
              );
              const hits = turf.lineIntersect(segment, holeOutline);
              if (hits.features.length > 0) {
                crossesHole = true;
                break;
              }
            }
            if (crossesHole) {
              message.error(MAP_HOLE_SEGMENT_CROSSES_HOLE_MESSAGE, 3);
              return;
            }
          }
        }

        cutoutVerticesRef.current = [...cutoutVerticesRef.current, pt];
        const verts = cutoutVerticesRef.current;
        setCutoutVertexCount(verts.length);
        updateCutoutVertexDots(verts);
        draw.delete(CUTOUT_PREVIEW_ID);
        if (verts.length >= 2) {
          draw.add({
            id: CUTOUT_PREVIEW_ID,
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: verts },
            properties: {},
          } as Feature<LineString>);
        }
        return;
      }
      if (modeRef.current === 'split') {
        e.stopImmediatePropagation();
        if (e.type === 'dblclick') return;
        const rect = canvas.getBoundingClientRect();
        const lngLat = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
        const pt: [number, number] = [lngLat.lng, lngLat.lat];
        if (!splitFirstPointRef.current) {
          splitFirstPointRef.current = pt;
          draw.add({
            id: SPLIT_PREVIEW_ID,
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [pt, pt] },
            properties: {},
          } as Feature<LineString>);
        } else {
          const start = splitFirstPointRef.current;
          splitFirstPointRef.current = null;
          draw.delete(SPLIT_PREVIEW_ID);
          const line: Feature<LineString> = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [start, pt] },
            properties: {},
          };
          const currentPaddock = selectedPaddockRef.current;
          if (!currentPaddock) return;

          const paddockFeature: Feature<Polygon> = {
            type: 'Feature',
            geometry: currentPaddock.geometry,
            properties: {},
          };
          const isValid = (() => {
            const result = splitPolygonByPolyline(paddockFeature, line);
            return result !== null && result.length >= 2;
          })();
          if (!isValid) {
            message.error(MAP_SPLIT_CUT_INVALID_MESSAGE, 3);
            return;
          }
          draw.add(line);
          splitLinesRef.current = [...splitLinesRef.current, line];
          setSplitLineCount(splitLinesRef.current.length);
        }
        return;
      }
      if (snapRedirecting) return;
      if (!snapEnabledRef.current || e.altKey || altKeyRef.current) return;
      if (modeRef.current !== 'draw') return;
      const target = snapTargetRef.current;
      if (!target) return;

      const rect = canvas.getBoundingClientRect();
      const px = map.project(target as mapboxgl.LngLatLike);
      e.stopImmediatePropagation();
      snapRedirecting = true;
      canvas.dispatchEvent(
        new MouseEvent(e.type, {
          bubbles: true, cancelable: true, view: window,
          clientX: rect.left + px.x, clientY: rect.top + px.y,
          button: 0, buttons: 0,
          ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, metaKey: e.metaKey,
          detail: e.detail,
        })
      );
      snapRedirecting = false;
    };

    const handleAltDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        altKeyRef.current = true;
        updateSnapUI(null);
      }
    };
    const handleAltUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') altKeyRef.current = false;
    };

    const handleRightDragDown = (e: MouseEvent) => {
      if (e.button === 0 && modeRef.current === 'edit') {
        leftDragActive = true;
      }
      if (e.button === 0 && (modeRef.current === 'split' || modeRef.current === 'cutout')) {
        e.stopImmediatePropagation();
        return;
      }
      if (e.button !== 2) return;
      if (modeRef.current !== 'edit') return;
      if (!selectedPaddockRef.current) return;
      e.stopImmediatePropagation();
      const rect = canvas.getBoundingClientRect();
      rightDragActive = true;
      rightButtonHeldRef.current = true;
      rightDragMoved = false;
      rightDragPreGeometry = draftGeometryRef.current?.geometry ?? selectedPaddockRef.current?.geometry ?? null;
      rightDragUndoCountAtStart = undoStackRef.current.length;
      rightDragLastLngLat = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
      canvas.style.cursor = CURSOR_RIGHT_DRAG_ACTIVE;
    };

    const handleRightDragMove = (e: MouseEvent) => {
      if (!rightDragActive && modeRef.current === 'edit' && (e.buttons & 2) === 0) {
        const rect = canvas.getBoundingClientRect();
        const px: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];
        const paddock = selectedPaddockRef.current;
        if (paddock) {
          const feature = draw.getAll().features.find(f => f.id === paddock.id);
          if (feature?.geometry.type === 'Polygon') {
            const ring = (feature.geometry as Polygon).coordinates[0];
            const nearHandle = ring.slice(0, -1).some((coord, i) => {
              const vPx = map.project(coord as mapboxgl.LngLatLike);
              if (Math.hypot(vPx.x - px[0], vPx.y - px[1]) < 10) return true;
              const next = ring[(i + 1) % (ring.length - 1)];
              const mPx = map.project([(coord[0] + next[0]) / 2, (coord[1] + next[1]) / 2] as mapboxgl.LngLatLike);
              return Math.hypot(mPx.x - px[0], mPx.y - px[1]) < 10;
            });
            canvas.style.cursor = nearHandle ? '' : CURSOR_RIGHT_DRAG_HOVER;
          }
        } else {
          canvas.style.cursor = '';
        }
      }
      if (!rightDragActive) return;
      if ((e.buttons & 2) === 0) {
        rightDragLastLngLat = null;
        canvas.style.cursor = '';
        return;
      }
      if (snapRedirecting) return;
      updateSnapUI(null);
      e.stopImmediatePropagation();
      const rect = canvas.getBoundingClientRect();
      const cur = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
      if (!rightDragLastLngLat) { rightDragLastLngLat = cur; return; }
      const δlng = cur.lng - rightDragLastLngLat.lng;
      const δlat = cur.lat - rightDragLastLngLat.lat;
      rightDragLastLngLat = cur;
      const paddock = selectedPaddockRef.current;
      if (!paddock) return;
      const feature = draw.getAll().features.find(f => f.id === paddock.id);
      if (!feature || feature.geometry.type !== 'Polygon') return;
      const translatedCoords = (feature.geometry as Polygon).coordinates.map(r => {
        const translated = r.map(c => [c[0] + δlng, c[1] + δlat] as [number, number]);
        translated[translated.length - 1] = [...translated[0]];
        return translated;
      });
      drawUpdateActive = true;
      draw.add({
        type: 'Feature',
        id: paddock.id,
        geometry: { type: 'Polygon', coordinates: translatedCoords },
        properties: feature.properties ?? {},
      });
      drawUpdateActive = false;
      rightDragMoved = true;
    };

    const handleRightDragUp = (e: MouseEvent) => {
      if (e.button === 0) { leftDragActive = false; return; }
      if (e.button !== 2 || !rightDragActive) return;
      rightDragActive = false;
      rightButtonHeldRef.current = false;
      rightDragLastLngLat = null;
      canvas.style.cursor = '';
      const paddock = selectedPaddockRef.current;
      if (!paddock) { rightDragMoved = false; rightDragPreGeometry = null; return; }
      const feature = draw.getAll().features.find(f => f.id === paddock.id) as Feature<Polygon> | undefined;

      const stack = undoStackRef.current;
      while (stack.length > rightDragUndoCountAtStart) {
        stack.pop();
      }

      if (feature && rightDragMoved && rightDragPreGeometry) {
        const postGeometry = feature.geometry as Polygon;
        const isNoNetChange = polygonsEqual(rightDragPreGeometry, postGeometry);
        const isDuplicate   = stack.length > 0 && polygonsEqual(stack[stack.length - 1], rightDragPreGeometry);
        if (!isNoNetChange && !isDuplicate) {
          stack.push(rightDragPreGeometry);
          redoStackRef.current = [];
          setCanUndo(true);
          setCanRedo(false);
          setUndoCount(stack.length);
          setRedoCount(0);
        }
        setDraftGeometry(feature);
      } else {
        setCanUndo(stack.length > 0);
        setUndoCount(stack.length);
      }

      rightDragMoved = false;
      rightDragPreGeometry = null;
    };

    const handleRightDragContextMenu = (e: MouseEvent) => {
      if (modeRef.current === 'edit') e.preventDefault();
    };

    const handleSplitMouseUp = (e: MouseEvent) => {
      if (e.button === 0 && (modeRef.current === 'split' || modeRef.current === 'cutout')) {
        e.stopImmediatePropagation();
      }
    };

    canvas.addEventListener('mousedown',   handleRightDragDown,        { capture: true });
    canvas.addEventListener('mouseup',     handleSplitMouseUp,         { capture: true });
    canvas.addEventListener('mousemove',   handleRightDragMove,        { capture: true });
    canvas.addEventListener('contextmenu', handleRightDragContextMenu, { capture: true });
    window.addEventListener('mouseup',     handleRightDragUp);
    canvas.addEventListener('mousemove', handleSnapMousemove, { capture: true });
    canvas.addEventListener('click',     handleSnapClick,    { capture: true });
    canvas.addEventListener('dblclick',  handleSnapClick,    { capture: true });
    window.addEventListener('keydown', handleAltDown);
    window.addEventListener('keyup',   handleAltUp);

    map.on('load', handleMapLoad);
    map.on('click', PADDOCK_FILL_LAYER_ID, handlePaddockClick);
    map.on('click', handleMapClick);
    map.on('contextmenu', handlePreventContextMenu);
    map.on('contextmenu', PADDOCK_FILL_LAYER_ID, handlePaddockContextMenu);
    map.on('move', handleDismissOverlays);
    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    map.on('draw.modechange', handleDrawModeChange);

    mapRef.current = map;
    drawRef.current = draw;

    // Resize map whenever the container changes size (e.g. sidebar toggle).
    const resizeObserver = new ResizeObserver(() => { map.resize(); });
    resizeObserver.observe(map.getContainer());

    return () => {
      resizeObserver.disconnect();
      canvas.removeEventListener('mousedown',   handleRightDragDown,        { capture: true });
      canvas.removeEventListener('mouseup',     handleSplitMouseUp,         { capture: true });
      canvas.removeEventListener('mousemove',   handleRightDragMove,        { capture: true });
      canvas.removeEventListener('contextmenu', handleRightDragContextMenu, { capture: true });
      window.removeEventListener('mouseup',     handleRightDragUp);
      canvas.removeEventListener('mousemove', handleSnapMousemove, { capture: true });
      canvas.removeEventListener('click',     handleSnapClick,    { capture: true });
      canvas.removeEventListener('dblclick',  handleSnapClick,    { capture: true });
      window.removeEventListener('keydown', handleAltDown);
      window.removeEventListener('keyup',   handleAltUp);
      map.off('load', handleMapLoad);
      map.off('click', PADDOCK_FILL_LAYER_ID, handlePaddockClick);
      map.off('click', handleMapClick);
      map.off('contextmenu', handlePreventContextMenu);
      map.off('contextmenu', PADDOCK_FILL_LAYER_ID, handlePaddockContextMenu);
      map.off('move', handleDismissOverlays);
      map.off('draw.create', handleDrawCreate);
      map.off('draw.update', handleDrawUpdate);
      map.off('draw.modechange', handleDrawModeChange);
      map.remove();
    };
  }, []);

  // ─── Keyboard events ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;

      if ((e.key === 'Delete' || e.key === 'Backspace') && mode === 'edit') {
        e.preventDefault();
        deleteSelectedVertex();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        if (mode === 'edit') { e.preventDefault(); handleUndoRef.current(); }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z') {
        if (mode === 'edit') { e.preventDefault(); handleRedoRef.current(); }
        return;
      }

      if (e.key === 'Enter' && mode === 'draw' && drawVerticesRef.current.length >= 3) {
        e.preventDefault();
        finalizePaddockDrawRef.current();
        return;
      }
      if (e.key === 'Enter' && mode === 'split' && splitLinesRef.current.length >= 1) {
        e.preventDefault();
        handleSplitApplyRef.current();
        return;
      }
      if (e.key === 'Enter' && mode === 'cutout' && cutoutVerticesRef.current.length >= 3) {
        e.preventDefault();
        finalizeCutoutRef.current();
        return;
      }

      if (
        e.key === 'Escape' &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        if (mode === 'draw' || mode === 'edit' || mode === 'merge' || mode === 'split' || mode === 'cutout') {
          if (mode === 'edit' && clearPaddockSelectionRef.current) {
            clearPaddockSelectionRef.current();
          } else if (mode === 'merge') {
            setMode('view');
            setPaddocksToMerge([]);
            paddocksToMergeRef.current = [];
            if (clearPaddockSelectionRef.current) clearPaddockSelectionRef.current();
          } else if (mode === 'split') {
            if (splitFirstPointRef.current) {
              splitFirstPointRef.current = null;
              drawRef.current?.delete(SPLIT_PREVIEW_ID);
            } else {
              setMode('view');
              if (clearPaddockSelectionRef.current) clearPaddockSelectionRef.current();
            }
          } else if (mode === 'draw') {
            if (drawVerticesRef.current.length > 0) {
              drawVerticesRef.current = [];
              setDrawVertexCount(0);
              drawRef.current?.delete(DRAW_PREVIEW_ID);
              (mapRef.current?.getSource('draw-vertices') as mapboxgl.GeoJSONSource | undefined)
                ?.setData({ type: 'FeatureCollection', features: [] });
            } else {
              resetDrawState();
            }
          } else if (mode === 'cutout') {
            if (cutoutVerticesRef.current.length > 0) {
              cutoutVerticesRef.current = [];
              setCutoutVertexCount(0);
              drawRef.current?.delete(CUTOUT_PREVIEW_ID);
              (mapRef.current?.getSource('cutout-vertices') as mapboxgl.GeoJSONSource | undefined)
                ?.setData({ type: 'FeatureCollection', features: [] });
            } else {
              setMode('view');
              if (clearPaddockSelectionRef.current) clearPaddockSelectionRef.current();
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mode]);

  // ─── Mode change effect ───────────────────────────────────────────────────
  useEffect(() => {
    const draw = drawRef.current;
    const map = mapRef.current;
    if (!draw || !map) return;

    const canvas = map.getCanvas();

    if (mode === 'split' || mode === 'cutout') {
      map.dragPan.disable();
      canvas.style.cursor = SPLIT_PEN_CURSOR;
    } else {
      map.dragPan.enable();
      canvas.style.cursor = '';
    }

    if (mode !== 'split') {
      splitFirstPointRef.current = null;
      splitLinesRef.current = [];
      setSplitLineCount(0);
    }

    if (mode !== 'cutout') {
      cutoutVerticesRef.current = [];
      setCutoutVertexCount(0);
      (mapRef.current?.getSource('cutout-vertices') as mapboxgl.GeoJSONSource | undefined)
        ?.setData({ type: 'FeatureCollection', features: [] });
    }

    const paddockPolyLayerIds = [PADDOCK_FILL_LAYER_ID, PADDOCK_OUTLINE_LAYER_ID];
    paddockPolyLayerIds.forEach(id => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(
          id,
          'visibility',
          mode === 'split' || mode === 'cutout' ? 'none' : 'visible'
        );
      }
    });
    if (map.getLayer(PADDOCK_LABELS_LAYER_ID)) {
      if ((mode === 'split' || mode === 'cutout') && selectedPaddock) {
        map.setFilter(PADDOCK_LABELS_LAYER_ID, ['==', ['get', 'id'], selectedPaddock.id]);
      } else {
        map.setFilter(PADDOCK_LABELS_LAYER_ID, null);
      }
    }

    draw.deleteAll();
    setDraftGeometry(null);
    snapTargetRef.current = null;
    (mapRef.current?.getSource('snap-indicator') as mapboxgl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: [] });
    drawVerticesRef.current = [];
    setDrawVertexCount(0);
    (mapRef.current?.getSource('draw-vertices') as mapboxgl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: [] });

    if (prevModeRef.current === 'edit' && mode !== 'edit') {
      const source = getPaddockSource(mapRef.current);
      if (source) source.setData(paddockFeatureCollectionRef.current);
    }
    if (prevModeRef.current === 'draw' && mode !== 'draw') {
      setDrawPaddockName(null);
    }

    if (mode === 'draw') {
      draw.changeMode('simple_select');
      draw.deleteAll();
    } else if (mode === 'edit' && selectedPaddock) {
      draw.deleteAll();
      draw.add({
        type: 'Feature',
        id: selectedPaddock.id,
        geometry: selectedPaddock.geometry,
        properties: {},
      });
      const bbox = turf.bbox(selectedPaddock.geometry);
      map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 120, maxZoom: 18, duration: 0 });
      draw.changeMode('direct_select', { featureId: selectedPaddock.id });
    } else if (mode === 'cutout') {
      if (selectedPaddock) {
        draw.add({
          type: 'Feature',
          id: selectedPaddock.id,
          geometry: selectedPaddock.geometry,
          properties: {},
        });
        const bbox = turf.bbox(selectedPaddock.geometry);
        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 120, maxZoom: 18, duration: 0 });
        draw.changeMode('simple_select', { featureIds: [selectedPaddock.id] });
      } else {
        draw.changeMode('simple_select');
      }
    } else if (mode === 'split') {
      if (selectedPaddock) {
        draw.add({
          type: 'Feature',
          id: selectedPaddock.id,
          geometry: selectedPaddock.geometry,
          properties: {},
        });
        const bbox = turf.bbox(selectedPaddock.geometry);
        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 120, maxZoom: 18, duration: 0 });
        draw.changeMode('simple_select', { featureIds: [selectedPaddock.id] });
      } else {
        draw.changeMode('simple_select');
      }
    } else if (mode === 'merge') {
      draw.changeMode('simple_select');
      draw.deleteAll();
      if (prevModeRef.current !== 'merge' && selectedPaddock) {
        const initial = [selectedPaddock];
        paddocksToMergeRef.current = initial;
        setPaddocksToMerge(initial);
        map.setFeatureState(
          { source: PADDOCK_SOURCE_ID, id: selectedPaddock.id },
          { selected: true }
        );
      }
    } else {
      draw.changeMode('simple_select');
      draw.deleteAll();
    }

    prevModeRef.current = mode;
  }, [mode, selectedPaddock, drawPaddockName]);

  // ─── Helper: reset draw state ─────────────────────────────────────────────
  function resetDrawState() {
    drawVerticesRef.current = [];
    setDrawVertexCount(0);
    drawRef.current?.delete(DRAW_PREVIEW_ID);
    (mapRef.current?.getSource('draw-vertices') as mapboxgl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: [] });
    drawRef.current?.deleteAll();
    setDrawPaddockName(null);
    setMode('view');
  }

  // ─── Create: from MapboxDraw draw.create event (fallback path) ───────────
  function handleConfirmDrawPaddock(feature: Feature<Polygon>) {
    const paddockName = drawPaddockNameRef.current;
    if (!paddockName) return;

    const mapInst = mapRef.current;
    let saveGeometry = feature.geometry;
    if (mapInst) {
      const candidates: [number, number][] = [];
      for (const f of paddockFeatureCollectionRef.current.features) {
        const r = f.geometry.coordinates[0];
        for (let i = 0; i < r.length - 1; i++) candidates.push([r[i][0], r[i][1]]);
      }
      if (candidates.length > 0) {
        const ring = feature.geometry.coordinates[0].map(c => [...c] as number[]);
        let corrected = false;
        for (let i = 0; i < ring.length - 1; i++) {
          const px = mapInst.project(ring[i] as mapboxgl.LngLatLike);
          const snapped = findSnapTarget(px, mapInst, candidates);
          if (snapped) {
            ring[i] = [...snapped];
            if (i === 0) ring[ring.length - 1] = [...snapped];
            corrected = true;
          }
        }
        if (corrected) saveGeometry = { type: 'Polygon', coordinates: [ring] };
      }
    }

    if (turf.kinks({ type: 'Feature', geometry: saveGeometry, properties: {} } as Feature<Polygon>).features.length > 0) {
      message.error(MAP_BOUNDARY_SELF_INTERSECTS_MESSAGE, 4);
      drawRef.current?.deleteAll();
      setMode('draw');
      return;
    }

    const areaInHectares = calculateAreaInHectares({ ...feature, geometry: saveGeometry });
    const newFeature = makeLocalFeature(`local-${Date.now()}`, paddockName, areaInHectares, saveGeometry);
    const updated = [...localFeaturesRef.current, newFeature];
    updateMapSources(updated, new Set([newFeature.id]));

    setDrawPaddockName(null);
    resetDrawState();

    if (mapRef.current) {
      mapRef.current.setFeatureState(
        { source: PADDOCK_SOURCE_ID, id: newFeature.id },
        { selected: true }
      );
    }
    setSelectedPaddock(newFeature);
  }

  // ─── Create: from custom vertex-collection draw flow ─────────────────────
  function finalizeDrawPaddock() {
    const verts = drawVerticesRef.current;
    drawVerticesRef.current = [];
    setDrawVertexCount(0);
    drawRef.current?.delete(DRAW_PREVIEW_ID);
    (mapRef.current?.getSource('draw-vertices') as mapboxgl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: [] });

    if (verts.length < 3) {
      if (verts.length > 0) message.error(MAP_BOUNDARY_MIN_POINTS_MESSAGE, 3);
      return;
    }

    const paddockName = drawPaddockNameRef.current;
    if (!paddockName) return;

    const ring: [number, number][] = [...verts, verts[0]];
    let saveGeometry: Polygon = { type: 'Polygon', coordinates: [ring] };

    const mapInst = mapRef.current;
    if (mapInst) {
      const candidates: [number, number][] = [];
      for (const f of paddockFeatureCollectionRef.current.features) {
        const r = f.geometry.coordinates[0];
        for (let i = 0; i < r.length - 1; i++) candidates.push([r[i][0], r[i][1]]);
      }
      if (candidates.length > 0) {
        const corrected = ring.map(c => [...c] as number[]);
        let changed = false;
        for (let i = 0; i < corrected.length - 1; i++) {
          const snapped = findSnapTarget(mapInst.project(corrected[i] as mapboxgl.LngLatLike), mapInst, candidates);
          if (snapped) {
            corrected[i] = [...snapped];
            if (i === 0) corrected[corrected.length - 1] = [...snapped];
            changed = true;
          }
        }
        if (changed) saveGeometry = { type: 'Polygon', coordinates: [corrected] };
      }
    }

    if (turf.kinks({ type: 'Feature', geometry: saveGeometry, properties: {} } as Feature<Polygon>).features.length > 0) {
      message.error(MAP_BOUNDARY_SELF_INTERSECTS_MESSAGE, 4);
      return;
    }

    const areaInHectares = calculateAreaInHectares(
      { type: 'Feature', geometry: saveGeometry, properties: {} } as Feature<Polygon>
    );

    const newFeature = makeLocalFeature(`local-${Date.now()}`, paddockName, areaInHectares, saveGeometry);
    const updated = [...localFeaturesRef.current, newFeature];
    updateMapSources(updated, new Set([newFeature.id]));

    setDrawPaddockName(null);
    setMode('view');
    drawRef.current?.deleteAll();

    if (mapRef.current) {
      mapRef.current.setFeatureState(
        { source: PADDOCK_SOURCE_ID, id: newFeature.id },
        { selected: true }
      );
    }
    setSelectedPaddock(newFeature);
  }

  // ─── Undo / Redo ─────────────────────────────────────────────────────────

  function getCurrentEditGeometry(): Polygon | null {
    return draftGeometryRef.current?.geometry ?? selectedPaddockRef.current?.geometry ?? null;
  }

  function applyEditGeometry(geometry: Polygon) {
    const paddock = selectedPaddockRef.current;
    const draw = drawRef.current;
    if (!paddock || !draw) return;
    const feature: Feature<Polygon> = { type: 'Feature', id: paddock.id, geometry, properties: {} };
    draw.add(feature);
    setDraftGeometry(feature);
    const source = getPaddockSource(mapRef.current);
    if (source) {
      source.setData({
        ...paddockFeatureCollectionRef.current,
        features: paddockFeatureCollectionRef.current.features.map(f =>
          f.properties?.id === paddock.id ? { ...f, geometry } : f
        ),
      });
    }
  }

  function clearUndoRedoStacks() {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
    setUndoCount(0);
    setRedoCount(0);
  }

  function pushToUndo() {
    if (rightButtonHeldRef.current) return;
    const current = getCurrentEditGeometry();
    if (!current) return;
    const stack = undoStackRef.current;
    if (stack.length > 0 && polygonsEqual(stack[stack.length - 1], current)) return;
    stack.push(current);
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
    setUndoCount(stack.length);
    setRedoCount(0);
  }

  function handleUndo() {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const prev = stack.pop()!;
    const cur = getCurrentEditGeometry();
    if (cur) { redoStackRef.current.push(cur); setCanRedo(true); }
    setCanUndo(stack.length > 0);
    setUndoCount(stack.length);
    setRedoCount(redoStackRef.current.length);
    applyEditGeometry(prev);
  }

  function handleRedo() {
    const stack = redoStackRef.current;
    if (!stack.length) return;
    const next = stack.pop()!;
    const cur = getCurrentEditGeometry();
    if (cur) { undoStackRef.current.push(cur); setCanUndo(true); }
    setCanRedo(stack.length > 0);
    setUndoCount(undoStackRef.current.length);
    setRedoCount(stack.length);
    applyEditGeometry(next);
  }

  function deleteSelectedVertex() {
    const draw = drawRef.current;
    const paddock = selectedPaddockRef.current;
    if (!draw || !paddock) return;

    const feature = draw.getAll().features.find(f => f.id === paddock.id);
    if (!feature || feature.geometry.type !== 'Polygon') return;
    const coords = (feature.geometry as Polygon).coordinates;

    let selectedPoints: ReturnType<typeof draw.getSelectedPoints>;
    try {
      selectedPoints = draw.getSelectedPoints();
    } catch {
      return;
    }
    if (!selectedPoints.features.length) return;

    const selCoord = (selectedPoints.features[0].geometry as GeoJSON.Point).coordinates as [number, number];

    let ringIdx = -1;
    let vertIdx = -1;
    outer: for (let r = 0; r < coords.length; r++) {
      const ring = coords[r];
      for (let i = 0; i < ring.length - 1; i++) {
        if (
          Math.abs(ring[i][0] - selCoord[0]) < 1e-10 &&
          Math.abs(ring[i][1] - selCoord[1]) < 1e-10
        ) {
          ringIdx = r;
          vertIdx = i;
          break outer;
        }
      }
    }
    if (ringIdx < 0) return;

    const ring = coords[ringIdx];
    const vertexCount = ring.length - 1;

    if (vertexCount <= 3) {
      if (ringIdx === 0) {
        message.error(MAP_VERTEX_DELETE_MIN_VERTICES_MESSAGE, 3);
        return;
      }
      const newCoords = coords.filter((_, idx) => idx !== ringIdx);
      const newGeometry: Polygon = { type: 'Polygon', coordinates: newCoords };
      pushToUndo();
      applyEditGeometry(newGeometry);
      return;
    }

    const newRing: number[][] = [];
    for (let i = 0; i < ring.length - 1; i++) {
      if (i !== vertIdx) newRing.push([...ring[i]]);
    }
    newRing.push([...newRing[0]]);

    const newCoords = coords.map((r, idx) => idx === ringIdx ? newRing : r);
    const newGeometry: Polygon = { type: 'Polygon', coordinates: newCoords };

    pushToUndo();
    applyEditGeometry(newGeometry);
  }

  // ─── Navigation ──────────────────────────────────────────────────────────

  function handleCenterAll() {
    const map = mapRef.current;
    if (!map) return;

    const feats = paddockFeatureCollectionRef.current.features;
    if (feats.length > 0) {
      const bbox = turf.bbox(paddockFeatureCollectionRef.current);
      map.fitBounds(
        [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
        { padding: 60, maxZoom: 18, duration: 0 }
      );
      return;
    }

    const fallbackAustralia = () => {
      map.fitBounds(
        [[113.0, -44.0], [154.0, -10.0]],
        { padding: 40, duration: 0 }
      );
    };

    if (!navigator.geolocation) {
      fallbackAustralia();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        map.flyTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: 14,
          duration: 0,
        });
      },
      fallbackAustralia,
      { timeout: 5000 }
    );
  }

  function handleCenterSelection() {
    const map = mapRef.current;
    if (!map || !selectedPaddock) return;

    const bbox = turf.bbox(selectedPaddock.geometry);
    map.fitBounds(
      [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      { padding: 120, maxZoom: 18, duration: 0 }
    );
  }

  // ─── Save edit ───────────────────────────────────────────────────────────

  function handleSavePaddock() {
    if (!selectedPaddock) return;
    const trimmedName = draftName.trim();
    if (!trimmedName) return;

    let geometry = draftGeometry?.geometry ?? selectedPaddock.geometry;

    if (draftGeometry && mapRef.current) {
      const map = mapRef.current;
      const candidates: [number, number][] = [];
      for (const f of paddockFeatureCollectionRef.current.features) {
        if (f.properties?.id === selectedPaddock.id) continue;
        const r = f.geometry.coordinates[0];
        for (let i = 0; i < r.length - 1; i++) candidates.push([r[i][0], r[i][1]]);
      }
      if (candidates.length > 0) {
        const ring = geometry.coordinates[0].map(c => [...c] as number[]);
        let corrected = false;
        for (let i = 0; i < ring.length - 1; i++) {
          const px = map.project(ring[i] as mapboxgl.LngLatLike);
          const snapped = findSnapTarget(px, map, candidates);
          if (snapped) {
            ring[i] = [...snapped];
            if (i === 0) ring[ring.length - 1] = [...snapped];
            corrected = true;
          }
        }
        if (corrected) geometry = { type: 'Polygon', coordinates: [ring, ...geometry.coordinates.slice(1)] };
      }
    }

    if (geometry.coordinates.length > 1) {
      const outerFeature: Feature<Polygon> = {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [geometry.coordinates[0]] },
        properties: {},
      };
      const holeFeatures = geometry.coordinates.slice(1).map(
        (ring): Feature<Polygon> => ({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] },
          properties: {},
        })
      );

      for (let i = 0; i < holeFeatures.length; i++) {
        if (!turf.booleanContains(outerFeature, holeFeatures[i])) {
          message.error(MAP_HOLE_OUTSIDE_BOUNDARY_MESSAGE(i), 4);
          return;
        }
      }

      for (let i = 0; i < holeFeatures.length; i++) {
        for (let j = i + 1; j < holeFeatures.length; j++) {
          if (turf.booleanIntersects(holeFeatures[i], holeFeatures[j])) {
            message.error(MAP_HOLES_OVERLAP_MESSAGE(i, j), 4);
            return;
          }
        }
      }
    }

    const areaInHectares = draftGeometry
      ? calculateAreaInHectares({ type: 'Feature', geometry, properties: {} } as Feature<Polygon>)
      : selectedPaddock.area;

    const updatedFeature: MapFeature = {
      ...selectedPaddock,
      name: trimmedName,
      geometry,
      area: areaInHectares,
    };
    const updated = localFeaturesRef.current.map(f =>
      f.id === selectedPaddock.id ? updatedFeature : f
    );
    updateMapSources(updated, new Set<string>());

    setMode('view');
    setDraftGeometry(null);
    setDraftName('');
    clearPaddockSelection();
  }

  // ─── Delete ──────────────────────────────────────────────────────────────

  function handleDeletePaddock() {
    if (!selectedPaddock) return;
    setShowDeleteModal(true);
  }

  function handleConfirmDelete() {
    if (!selectedPaddock) return;
    setShowDeleteModal(false);
    const updated = localFeaturesRef.current.filter(f => f.id !== selectedPaddock.id);
    updateMapSources(updated, new Set<string>());
    setSelectedPaddock(null);
  }

  // ─── Merge ───────────────────────────────────────────────────────────────

  function handleMergePaddocks(paddocks: MapFeature[], mergedName: string) {
    if (paddocks.length < 2) return;
    const merged = mergePaddockGeometriesMultiple(paddocks.map(p => p.geometry));
    if (!merged) {
      setShowMergeConfirmModal(false);
      return;
    }
    const areaInHectares = Number(
      (turf.area(turf.polygon(merged.coordinates)) / 10_000).toFixed(2)
    );

    setShowMergeConfirmModal(false);
    setPaddocksToMerge([]);
    paddocksToMergeRef.current = [];
    setMode('view');
    deselectAllFeatures(mapRef.current!, paddockFeatureCollectionRef.current);
    setSelectedPaddock(null);

    const newFeature = makeLocalFeature(`local-${Date.now()}`, mergedName, areaInHectares, merged);
    const ids = new Set(paddocks.map(p => p.id));
    const updated = [...localFeaturesRef.current.filter(f => !ids.has(f.id)), newFeature];
    updateMapSources(updated, new Set<string>());
  }

  // ─── Split ───────────────────────────────────────────────────────────────

  function handleSplitApply() {
    const paddock = selectedPaddockRef.current;
    const lines = splitLinesRef.current;
    if (!paddock || lines.length === 0) return;

    let pieces: Feature<Polygon>[] = [{
      type: 'Feature',
      geometry: paddock.geometry,
      properties: {},
    }];

    for (const line of lines) {
      const next: Feature<Polygon>[] = [];
      for (const poly of pieces) {
        const result = splitPolygonByPolyline(poly, line);
        if (result && result.length >= 2) {
          next.push(...result);
        } else {
          next.push(poly);
        }
      }
      pieces = next;
    }

    if (pieces.length < 2) {
      message.error(MAP_SPLIT_NO_VALID_CUTS_MESSAGE, 3);
      return;
    }

    setMode('view');
    deselectAllFeatures(mapRef.current!, paddockFeatureCollectionRef.current);
    setSelectedPaddock(null);
    drawRef.current?.deleteAll();

    const baseId = Date.now();
    const newFeatures = pieces.map((poly, i) =>
      makeLocalFeature(`local-${baseId + i}`, `${paddock.name} – ${i + 1}`, calculateAreaInHectares(poly), poly.geometry)
    );
    const updated = [
      ...localFeaturesRef.current.filter(f => f.id !== paddock.id),
      ...newFeatures,
    ];
    updateMapSources(updated, new Set<string>());
  }

  // ─── Cutout ──────────────────────────────────────────────────────────────

  function finalizeCutout() {
    const verts = cutoutVerticesRef.current;
    cutoutVerticesRef.current = [];
    setCutoutVertexCount(0);
    drawRef.current?.delete(CUTOUT_PREVIEW_ID);
    (mapRef.current?.getSource('cutout-vertices') as mapboxgl.GeoJSONSource | undefined)
      ?.setData({ type: 'FeatureCollection', features: [] });

    if (verts.length < 3) {
      if (verts.length > 0) {
        message.error(MAP_CUTOUT_MIN_POINTS_MESSAGE, 3);
      }
      return;
    }

    const paddock = selectedPaddockRef.current;
    if (!paddock) return;

    const ring: [number, number][] = [...verts, verts[0]];

    const holePolygon: Feature<Polygon> = {
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [ring] },
      properties: {},
    };

    if (turf.kinks(holePolygon).features.length > 0) {
      message.error(MAP_CUTOUT_SELF_INTERSECTS_MESSAGE, 4);
      return;
    }

    const outerOnly: Feature<Polygon> = {
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [paddock.geometry.coordinates[0]] },
      properties: {},
    };
    if (!turf.booleanContains(outerOnly, holePolygon)) {
      message.error(MAP_CUTOUT_OUTSIDE_BOUNDARY_MESSAGE, 3);
      return;
    }

    const newGeometry: Polygon = {
      type: 'Polygon',
      coordinates: [
        paddock.geometry.coordinates[0],
        ...paddock.geometry.coordinates.slice(1),
        ring,
      ],
    };

    const rewound = turf.rewind(
      { type: 'Feature', geometry: newGeometry, properties: {} } as Feature<Polygon>,
      { mutate: false }
    ) as Feature<Polygon>;

    const areaInHectares = calculateAreaInHectares(rewound);

    setMode('view');
    deselectAllFeatures(mapRef.current!, paddockFeatureCollectionRef.current);
    setSelectedPaddock(null);
    drawRef.current?.deleteAll();

    const updatedFeature: MapFeature = {
      ...paddock,
      area: areaInHectares,
      geometry: rewound.geometry,
    };
    const updated = localFeaturesRef.current.map(f =>
      f.id === paddock.id ? updatedFeature : f
    );
    updateMapSources(updated, new Set<string>());
  }

  // ─── Rename ──────────────────────────────────────────────────────────────

  function handleRenameStart() {
    if (selectedPaddock) setRenamePaddock(selectedPaddock);
  }

  function handleRenameSave(newName: string) {
    if (!renamePaddock) return;
    const updatedFeature: MapFeature = { ...renamePaddock, name: newName };
    const updated = localFeaturesRef.current.map(f =>
      f.id === renamePaddock.id ? updatedFeature : f
    );
    updateMapSources(updated, new Set<string>());
    setRenamePaddock(null);
    setSelectedPaddock(prev =>
      prev?.id === renamePaddock.id ? updatedFeature : prev
    );
  }

  // ─── Update Area ─────────────────────────────────────────────────────────

  function handleUpdateAreaStart() {
    if (selectedPaddock) setUpdateAreaPaddock(selectedPaddock);
  }

  function handleUpdateAreaSave(area: number) {
    if (!updateAreaPaddock) return;
    const updatedFeature: MapFeature = { ...updateAreaPaddock, area };
    const updated = localFeaturesRef.current.map(f =>
      f.id === updateAreaPaddock.id ? updatedFeature : f
    );
    updateMapSources(updated, new Set<string>());
    setUpdateAreaPaddock(null);
    setSelectedPaddock(prev =>
      prev?.id === updateAreaPaddock.id ? updatedFeature : prev
    );
  }

  // ─── Clear selection ─────────────────────────────────────────────────────

  function clearPaddockSelection() {
    const map = mapRef.current;
    const draw = drawRef.current;
    if (!map) return;

    clearUndoRedoStacks();
    deselectAllFeatures(map, paddockFeatureCollectionRef.current);

    setSelectedPaddock(null);
    setDraftGeometry(null);
    setDraftName('');
    setPaddocksToMerge([]);
    paddocksToMergeRef.current = [];

    if (draw) {
      exitingEditRef.current = true;
      draw.deleteAll();
      exitingEditRef.current = false;
    }

    if (modeRef.current !== 'view') {
      setMode('view');
    }
  }

  // Store function references in refs for use in event handlers
  useEffect(() => {
    clearPaddockSelectionRef.current = clearPaddockSelection;
    handleSplitApplyRef.current = handleSplitApply;
    finalizeCutoutRef.current = finalizeCutout;
    finalizePaddockDrawRef.current = finalizeDrawPaddock;
    handleConfirmDrawPaddockRef.current = handleConfirmDrawPaddock;
    pushToUndoRef.current = pushToUndo;
    handleUndoRef.current = handleUndo;
    handleRedoRef.current = handleRedo;
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Merge modal helpers
  const isMergeAdjacent =
    showMergeConfirmModal && paddocksToMerge.length >= 2
      ? mergePaddockGeometriesMultiple(paddocksToMerge.map(p => p.geometry)) !== null
      : true;
  const suggestedPrefix = computeSuggestedMergeName(paddocksToMerge);
  const CUSTOM_KEY = '__custom__';
  const isCustom = mergeSelectedName === CUSTOM_KEY;
  const effectiveName = isCustom ? mergeCustomName : mergeSelectedName;
  const nameEmpty = effectiveName.trim() === '';
  const uniqueNames = Array.from(new Set(paddocksToMerge.map(p => p.name)));
  const totalRows = (suggestedPrefix ? 1 : 0) + uniqueNames.length + 1;
  const MAX_VISIBLE_ROWS = 10;

  return (
    <div className="relative h-full w-full">
      {/* Map canvas container — must stay empty so mapbox-gl can own its DOM */}
      <div id={PADDOCK_MAP_CONTAINER_ID} className="h-full w-full" />

      {/* AntD Modals render via portal to document.body — safe to keep anywhere */}
      {/* Step 1 of "New Paddock" — enter name before drawing */}
      {mode === 'draw' && drawPaddockName === null && (
        <PaddockNewNameModal
          onConfirm={name => { setDrawPaddockName(name); setDraftName(name); }}
          onCancel={() => setMode('view')}
        />
      )}

      {/* Delete confirmation modal */}
      <Modal
        open={showDeleteModal}
        title="Delete paddock?"
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
        mask={{ closable: true }}
        onOk={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      >
        <p className="text-gray-dark">
          <span className="font-semibold">{selectedPaddock?.name}</span>
          {' '}will be permanently removed. This cannot be undone.
        </p>
      </Modal>

      {/* Merge confirmation modal */}
      <Modal
        title="Merge Paddocks"
        open={showMergeConfirmModal}
        onCancel={() => setShowMergeConfirmModal(false)}
        onOk={() => {
          if (nameEmpty) return;
          handleMergePaddocks(paddocksToMerge, effectiveName.trim());
        }}
        okText="Merge"
        cancelText="Cancel"
        okButtonProps={{ disabled: !isMergeAdjacent || nameEmpty }}
        mask={{ closable: false }}
        destroyOnHidden
      >
        <p className="mb-3 text-sm text-gray-dark">
          Merge {paddocksToMerge.length} paddocks into one. Choose a name:
        </p>
        <div className={totalRows > MAX_VISIBLE_ROWS ? 'max-h-[18rem] overflow-y-auto pr-2' : ''}>
          <Radio.Group
            className="flex flex-col gap-2"
            value={mergeSelectedName}
            onChange={e => setMergeSelectedName(e.target.value)}
          >
            {suggestedPrefix && (
              <Radio value={suggestedPrefix}>{suggestedPrefix}</Radio>
            )}
            {uniqueNames.map(name => (
              <Radio key={name} value={name}>{name}</Radio>
            ))}
            <Radio value={CUSTOM_KEY}>
              <span className="inline-flex items-center gap-2">
                Custom name
                {isCustom && (
                  <input
                    type="text"
                    autoFocus
                    value={mergeCustomName}
                    onChange={e => setMergeCustomName(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    placeholder="New paddock name"
                    className={[
                      'w-[11.25rem] rounded border px-2 py-0.5 text-sm outline-none focus:ring-1',
                      mergeCustomName.trim()
                        ? 'border-gray-300 focus:ring-success'
                        : 'border-red-400 focus:ring-red-400',
                    ].join(' ')}
                  />
                )}
              </span>
            </Radio>
          </Radio.Group>
        </div>
        {isCustom && nameEmpty && (
          <p className="mt-1.5 text-xs text-red-500">Name cannot be empty.</p>
        )}
        {!isMergeAdjacent && (
          <p className="mt-3 text-sm text-red-500">
            These paddocks are not adjacent. Only touching or overlapping
            paddocks can be merged.
          </p>
        )}
      </Modal>

      {renamePaddock && (
        <PaddockRenameModal
          paddock={renamePaddock}
          onSave={handleRenameSave}
          onCancel={() => setRenamePaddock(null)}
        />
      )}

      {updateAreaPaddock && (
        <PaddockUpdateAreaModal
          paddock={updateAreaPaddock}
          onSave={handleUpdateAreaSave}
          onCancel={() => setUpdateAreaPaddock(null)}
        />
      )}

      {/* Overlay layer — pointer-events-none so the map canvas stays interactive.
          Each interactive child opts back in with pointer-events-auto. */}
      <div className="pointer-events-none absolute inset-0">

        {/* Paddock editing toolbar */}
        <div className="pointer-events-auto">
        <MapToolbar
          mode={mode}
          setMode={setMode}
          isLoading={isLoading}
          selectedPaddock={selectedPaddock}
          paddocksToMerge={paddocksToMerge}
          draftName={draftName}
          snapEnabled={snapEnabled}
          onSnapToggle={() => setSnapEnabled(s => !s)}
          onCenterAll={handleCenterAll}
          onCenterSelection={handleCenterSelection}
          onRequestMergeConfirm={() => {
            const suggested = computeSuggestedMergeName(paddocksToMerge);
            setMergeSelectedName(suggested ?? paddocksToMerge[0]?.name ?? '');
            setMergeCustomName('');
            setShowMergeConfirmModal(true);
          }}
          handleSavePaddock={handleSavePaddock}
          handleDeletePaddock={handleDeletePaddock}
          handleRenamePaddock={handleRenameStart}
          handleUpdateArea={handleUpdateAreaStart}
          clearPaddockSelection={clearPaddockSelection}
          canUndo={canUndo}
          canRedo={canRedo}
          undoCount={undoCount}
          redoCount={redoCount}
          onUndo={handleUndo}
          onRedo={handleRedo}
          splitLineCount={splitLineCount}
          onRequestSplitApply={() => handleSplitApplyRef.current()}
          cutoutVertexCount={cutoutVertexCount}
          onRequestCutoutFinalize={() => finalizeCutoutRef.current()}
          drawVertexCount={drawVertexCount}
          onRequestDrawFinalize={() => finalizePaddockDrawRef.current()}
        />
        </div>

        {/* Overlap disambiguation dropdown */}
        {disambiguate && (
          <div className="pointer-events-auto">
            <PaddockDisambiguateMenu
              x={disambiguate.x}
              y={disambiguate.y}
              paddocks={disambiguate.paddocks}
              onHover={paddock => {
                const map = mapRef.current;
                if (!map) return;
                deselectAllFeatures(map, paddockFeatureCollectionRef.current);
                if (paddock) {
                  map.setFeatureState(
                    { source: PADDOCK_SOURCE_ID, id: paddock.id },
                    { selected: true }
                  );
                }
              }}
              onSelect={paddock => {
                setDisambiguate(null);
                const map = mapRef.current;
                if (!map) return;
                deselectAllFeatures(map, paddockFeatureCollectionRef.current);
                map.setFeatureState(
                  { source: PADDOCK_SOURCE_ID, id: paddock.id },
                  { selected: true }
                );
                setSelectedPaddock(paddock);
                if (paddock.paddockId) {
                  onPaddockSelect(paddock.paddockId);
                }
              }}
              onClose={() => {
                setDisambiguate(null);
                const map = mapRef.current;
                if (map) deselectAllFeatures(map, paddockFeatureCollectionRef.current);
              }}
            />
          </div>
        )}

        {/* Right-click context menu — view mode only */}
        {contextMenu && (
          <div className="pointer-events-auto">
            <PaddockContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              onClose={() => setContextMenu(null)}
              onCenter={handleCenterSelection}
              onRename={handleRenameStart}
              onUpdateArea={handleUpdateAreaStart}
              onEdit={() => setMode('edit')}
              mergeDisabled={false}
              onMerge={() => setMode('merge')}
              onSplit={() => setMode('split')}
              onCutout={() => setMode('cutout')}
              onDelete={handleDeletePaddock}
            />
          </div>
        )}

        {/* Right-side vertical toolbar — zoom, compass, geolocate */}
        <div className="pointer-events-auto">
          <MapNavToolbar mapRef={mapRef} />
        </div>

        {/* Attribution info button — bottom-right */}
        <div className="pointer-events-auto">
          <MapAttributionButton />
        </div>

        {/* Paddock details panel — left side slide-in */}
        {(selectedPaddock || (mode === 'draw' && drawPaddockName !== null)) && (
          <div className="pointer-events-auto">
            <PaddockInfoPanel
              selectedPaddock={
                selectedPaddock ??
                makeLocalFeature('__new__', drawPaddockName!, 0, {
                  type: 'Polygon',
                  coordinates: [],
                } as Polygon)
              }
              mode={mode}
              draftName={draftName}
              onDraftNameChange={name => {
                setDraftName(name);
                if (mode === 'draw') setDrawPaddockName(name || null);
              }}
              onClose={mode === 'draw' ? resetDrawState : clearPaddockSelection}
              draftGeometry={draftGeometry?.geometry ?? null}
            />
          </div>
        )}

        {/* Map tips — shown on first load, fades after 3 s */}
        {mapTipsVisible && mode === 'view' && (
          <div
            className={`absolute bottom-4 left-1/2 z-10 -translate-x-1/2 transition-opacity duration-500 ${mapTipsFading ? 'opacity-0' : 'opacity-100'}`}
          >
            <div className="rounded-2xl map-hint-pill px-5 py-2 text-center text-sm font-medium text-charcoal shadow-sm backdrop-blur-sm">
              <div>Click to select  ·  Drag to pan  ·  Scroll to zoom</div>
              <div className="mt-0.5 text-xs text-gray-muted">Right-drag to rotate &amp; tilt view</div>
            </div>
          </div>
        )}

        {/* Status bar — mode hints at bottom centre */}
        {(() => {
          let line1: React.ReactNode = null;
          let line2: React.ReactNode = null;
          let line3: React.ReactNode = null;

          if (mode === 'edit') {
            const hasChange = canUndo || (selectedPaddock !== null && draftName.trim() !== selectedPaddock.name);
            if (hasChange) {
              line1 = 'Boundary modified — click Save to confirm.';
              line2 = (
                <span className="inline-flex items-center gap-1">
                  <ShortcutHint shortcutKey="Z" variant="dark" /> undo ·{' '}
                  <ShortcutKey variant="dark">Esc</ShortcutKey> to cancel
                </span>
              );
            } else {
              line1 =
                'Drag vertices to reshape  ·  Right-click drag to move';
              line2 = (
                <span className="inline-flex items-center gap-1">
                  <ShortcutHint shortcutKey="Z" variant="dark" /> undo ·{' '}
                  <ShortcutKey variant="dark">Ctrl</ShortcutKey>
                  <span>/</span>
                  <ShortcutKey variant="dark">
                    <CmdKey />
                  </ShortcutKey>
                  <span>+</span>
                  <ShortcutKey variant="dark">Shift</ShortcutKey>
                  <span>+</span>
                  <ShortcutKey variant="dark">Z</ShortcutKey>
                  {' '}redo
                </span>
              );
              line3 = (
                <span className="inline-flex items-center gap-1">
                  Click vertex +{' '}
                  <ShortcutKey variant="dark">Del</ShortcutKey> to delete ·{' '}
                  <ShortcutKey variant="dark">Esc</ShortcutKey> to cancel
                </span>
              );
            }
          } else if (mode === 'merge') {
            line1 = paddocksToMerge.length >= 2
              ? `${paddocksToMerge.length} paddocks selected — click Merge to confirm.`
              : paddocksToMerge.length === 1
              ? 'Click an adjacent paddock to add it to the merge.'
              : 'Click a paddock to start merging.';
          } else if (mode === 'draw' && drawPaddockName !== null) {
            line1 = drawVertexCount === 0
              ? 'Click to place the first vertex.'
              : drawVertexCount < 3
              ? `${drawVertexCount} point${drawVertexCount > 1 ? 's' : ''} placed — add at least ${3 - drawVertexCount} more.`
              : (
                <span className="inline-flex items-center gap-1">
                  {drawVertexCount} points — click to continue,{' '}
                  <ShortcutKey variant="dark">Enter</ShortcutKey> or Finish to
                  save.
                </span>
              );
          } else if (mode === 'split') {
            line1 = splitLineCount === 0
              ? 'Click two points to draw a cut line across the paddock.'
              : `${splitLineCount} cut line${splitLineCount > 1 ? 's' : ''} ready — click Apply or draw another.`;
          } else if (mode === 'cutout') {
            line1 = cutoutVertexCount === 0
              ? 'Click inside the paddock to start drawing the cutout polygon.'
              : cutoutVertexCount < 3
              ? `${cutoutVertexCount} point${cutoutVertexCount > 1 ? 's' : ''} placed — add at least ${3 - cutoutVertexCount} more.`
              : (
                <span className="inline-flex items-center gap-1">
                  {cutoutVertexCount} points — click to continue,{' '}
                  <ShortcutKey variant="dark">Enter</ShortcutKey> or Finish to
                  save.
                </span>
              );
          }

          if (!line1) return null;
          return (
            <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
              <div className="rounded-2xl map-hint-pill px-5 py-2 text-center text-sm font-medium text-charcoal shadow-sm backdrop-blur-sm">
                <div>{line1}</div>
                {line2 && (
                  <div className="mt-0.5 text-xs text-gray-muted">{line2}</div>
                )}
                {line3 && (
                  <div className="mt-0.5 text-xs text-gray-muted">{line3}</div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default PaddockMapCanvas;
